import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, ClipboardText, ArrowRight, Package,
  Storefront, FilePdf, ImageSquare, LinkSimple as LinkIcon, User as UserIcon,
  CaretDown, CaretRight, Lightning, Star, Printer, CheckSquare, Square,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { shizaiApi, REQUEST_STATUS_BADGE, REQUEST_STATUSES, PRIORITY_BADGE } from './shizaiApi';
import { StorageThumb, StorageLink } from '@/components/StorageMedia';
import CategoryEditDialog from './CategoryEditDialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const hasAttachment = (r) => (r.lines || []).some((l) => l.photo_url || l.reference_url);
const attachmentCount = (r) => (r.lines || []).filter((l) => l.photo_url).length;

export default function Requests() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canEditShizai(user);
  const [list, setList] = useState([]);
  const [status, setStatus] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // all | parts_request | buyer
  const [orderCategories, setOrderCategories] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [suppliers, setSuppliers] = useState([]);
  // Header-level 受注分類 quick-edit dialog state (Feb 2026): auto-generated
  // REQs (WO release / 補充提案 / parts_request) usually land with no
  // category, so we surface an inline edit chip on every card.
  const [catEditReq, setCatEditReq] = useState(null);
  // Bulk-select state (Feb 2026): admin can multi-select REQs on this list
  // and set a common 受注分類 in one shot — useful for cleaning up WO batches
  // that came in without categories.
  const [selected, setSelected] = useState(() => new Set());
  const [catBulkOpen, setCatBulkOpen] = useState(false);

  const load = useCallback(() => {
    shizaiApi.requests(status === 'all' ? {} : { status }).then(setList).catch((err) => console.error('[shizai] requests failed', err));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { shizaiApi.orderCategories().then(setOrderCategories).catch(() => {}); }, []);
  useEffect(() => { shizaiApi.suppliers().then(setSuppliers).catch(() => {}); }, []);

  const visibleList = useMemo(() => {
    if (sourceFilter === 'all') return list;
    if (sourceFilter === 'parts_request') return list.filter((r) => r.source === 'parts_request');
    return list.filter((r) => (r.source || 'buyer') !== 'parts_request');
  }, [list, sourceFilter]);

  const partsCount = useMemo(() => list.filter((r) => r.source === 'parts_request').length, [list]);

  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Create/edit now live on a dedicated page — this screen only browses.
  const openNew = () => navigate('/shizai/requests/new');
  const openEdit = (r) => navigate(`/shizai/requests/${r.id}/edit`);

  const del = async (r) => {
    if (!window.confirm(`${r.request_no} を削除しますか？`)) return;
    try { await shizaiApi.deleteRequest(r.id); toast.success(t('shizai.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
  };
  const placeOrder = (r) => navigate('/shizai/orders', { state: { request: r } });

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-requests">
      <PageHeader
        testid="requests-header"
        title="依頼一覧"
        description="登録済みの資材依頼を確認・編集します"
        primary={canWrite ? (
          <Button data-testid="request-new-btn" onClick={openNew} className="h-10">
            <Plus size={17} className="mr-1.5" weight="bold" /> {t('shizai.req.new')}
          </Button>
        ) : null}
      />

      {/* Status filter */}
      <div className="flex gap-1.5 flex-wrap">
        {['all', ...REQUEST_STATUSES].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`request-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              status === s ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
            {s === 'all' ? t('shizai.all') : t(`shizai.reqStatus.${s}`)}
          </button>
        ))}
      </div>

      {/* Source filter — only shown when there is at least one parts_request submission */}
      {partsCount > 0 && (
        <div className="flex gap-1.5 flex-wrap items-center" data-testid="request-source-filter">
          <span className="text-xs font-semibold text-zinc-500 mr-1">{t('shizai.req.filterSource')}:</span>
          {[
            { k: 'all', label: t('shizai.req.filterAllSource'), cls: 'bg-teal-700 text-white border-teal-700' },
            { k: 'parts_request', label: `${t('shizai.req.filterPartsOnly')} (${partsCount})`, cls: 'bg-zinc-700 text-white border-zinc-700' },
            { k: 'buyer', label: t('shizai.req.filterBuyerOnly'), cls: 'bg-zinc-700 text-white border-zinc-700' },
          ].map(({ k, label, cls }) => (
            <button key={k} onClick={() => setSourceFilter(k)} data-testid={`request-source-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                sourceFilter === k ? cls : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Bulk-select action bar — visible only when >=1 REQ selected */}
      {canWrite && selected.size > 0 && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm sticky top-2 z-10" data-testid="requests-bulk-bar">
          <span className="text-sm font-medium text-zinc-900">選択中 {selected.size}件</span>
          <span className="text-xs text-zinc-500">複数の依頼にまとめて操作</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}
            data-testid="requests-bulk-clear" className="h-9">
            選択解除
          </Button>
          <Button size="sm" onClick={() => setCatBulkOpen(true)}
            data-testid="requests-bulk-cat-btn"
            className="h-9 bg-teal-700 hover:bg-teal-800 shadow-sm">
            分類を一括設定 ({selected.size})
          </Button>
        </div>
      )}

      {/* Card-based list */}
      <div className="space-y-3" data-testid="request-table">
        {visibleList.length === 0 ? (
          <EmptyState
            icon={ClipboardText}
            title={t('shizai.req.empty')}
            description={canWrite ? '「新規依頼」から最初の依頼を登録できます' : undefined}
            action={canWrite ? (
              <Button variant="outline" onClick={openNew} data-testid="request-empty-new-btn" className="h-10">
                <Plus size={15} weight="bold" className="mr-1.5" /> {t('shizai.req.new')}
              </Button>
            ) : null}
            testid="request-empty"
          />
        ) : visibleList.map((r) => (
          <RequestCard
            key={r.id} req={r} t={t}
            expanded={expanded.has(r.id)}
            onExpand={() => toggleExpand(r.id)}
            canWrite={canWrite}
            onPlace={() => placeOrder(r)}
            onEdit={() => openEdit(r)}
            onDelete={() => del(r)}
            onReload={load}
            onEditCategory={setCatEditReq}
            suppliers={suppliers}
            selected={selected.has(r.id)}
            onSelectToggle={(v) => setSelected((prev) => {
              const next = new Set(prev);
              if (v) next.add(r.id); else next.delete(r.id);
              return next;
            })}
          />
        ))}
      </div>

      {/* Header-level 受注分類 quick-edit dialog (Feb 2026) */}
      {catEditReq && (
        <CategoryEditDialog
          req={catEditReq}
          categories={orderCategories}
          onClose={() => setCatEditReq(null)}
          onSaved={() => { setCatEditReq(null); load(); }}
        />
      )}

      {/* Bulk 受注分類 assign — kicked off from the sticky bulk-select bar */}
      {catBulkOpen && (() => {
        const ids = Array.from(selected);
        const chosen = visibleList.filter((r) => selected.has(r.id));
        const hint = chosen.slice(0, 3).map((r) => r.request_no).join('、')
          + (chosen.length > 3 ? ` 他${chosen.length - 3}件` : '');
        return (
          <CategoryEditDialog
            bulk={{ ids, count: ids.length, hint, orderType: 'purchase' }}
            categories={orderCategories}
            onClose={() => setCatBulkOpen(false)}
            onSaved={() => { setCatBulkOpen(false); setSelected(new Set()); load(); }}
          />
        );
      })()}
    </div>
  );
}

/**
 * Rich per-request card. Renders a compact summary row + an expandable
 * inline detail block showing all lines (with attachments/URL) so the buyer
 * can review a 部品受注入力 (parts_request) submission without leaving the list.
 */
function RequestCard({ req, t, expanded, onExpand, canWrite, onPlace, onEdit, onDelete, onReload, onEditCategory, suppliers, selected, onSelectToggle }) {
  const isParts = req.source === 'parts_request';
  const canOrder = req.status === 'open';
  const priorityCls = PRIORITY_BADGE[req.priority] || PRIORITY_BADGE.normal;
  const statusCls = REQUEST_STATUS_BADGE[req.status];
  const attachCount = attachmentCount(req);
  const anyAttach = hasAttachment(req);
  const first = (req.lines || [])[0] || {};
  const otherCount = (req.line_count || (req.lines || []).length || 0) - 1;

  // Per-line supplier suggestions — fetched lazily when the card expands.
  // Keyed by line_index so the detail component can look up quickly.
  const [suggestByLine, setSuggestByLine] = useState({});
  const [suggestLoading, setSuggestLoading] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    if (req.status === 'ordered') return;   // nothing to suggest for fully-ordered REQs
    // Only fetch if there's at least one open line without a supplier
    const anyOpenNoSup = (req.lines || []).some(
      (ln) => ln.line_status !== 'ordered' && !ln.supplier_id,
    );
    if (!anyOpenNoSup) return;
    let cancelled = false;
    setSuggestLoading(true);
    shizaiApi.supplierSuggestions(req.id)
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data.suggestions_by_line || []).forEach((row) => {
          if ((row.candidates || []).length > 0) map[row.line_index] = row.candidates;
        });
        setSuggestByLine(map);
      })
      .catch(() => { /* silent — a missing suggestion is not a UX blocker */ })
      .finally(() => { if (!cancelled) setSuggestLoading(false); });
    return () => { cancelled = true; };
  }, [expanded, req.id, req.status, req.lines]);

  // Techs入力済み toggle — legacy Techs システムへの転記トラッキング
  const [techsBusy, setTechsBusy] = useState(false);
  const toggleTechs = useCallback(async (e) => {
    e.stopPropagation();
    if (techsBusy) return;
    setTechsBusy(true);
    try {
      await shizaiApi.patchTechsEntered(req.id, !req.techs_entered);
      toast.success(!req.techs_entered ? 'Techs入力済みにしました' : 'Techs未入力に戻しました');
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '更新に失敗しました');
    } finally {
      setTechsBusy(false);
    }
  }, [req.id, req.techs_entered, techsBusy, onReload]);

  const applySupplier = useCallback(async (line_index, supplier_id, supplier_name) => {
    try {
      await shizaiApi.patchLineSupplier(req.id, line_index, supplier_id);
      toast.success(`#${line_index + 1} を「${supplier_name}」に設定しました`);
      // Clear the suggestion for this line so the chip row disappears
      setSuggestByLine((prev) => {
        const next = { ...prev };
        delete next[line_index];
        return next;
      });
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '仕入先の設定に失敗しました');
    }
  }, [req.id, onReload]);

  // 仕入先の付け替え (per-line picker in the expanded REQ view): change or
  // clear the supplier of any OPEN line directly on the 発注依頼 page.
  const changeSupplier = useCallback(async (line_index, supplier_id) => {
    const sname = supplier_id ? ((suppliers || []).find((s) => s.id === supplier_id)?.name || '') : '未指定';
    try {
      await shizaiApi.patchLineSupplier(req.id, line_index, supplier_id || null);
      toast.success(`#${line_index + 1} の仕入先を「${sname}」に変更しました`);
      setSuggestByLine((prev) => {
        const next = { ...prev };
        delete next[line_index];
        return next;
      });
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '仕入先の変更に失敗しました');
    }
  }, [req.id, suppliers, onReload]);

  // 発注済み明細の仕入先変更 (Feb 2026): confirm-first flow — flips the PO to
  // 取消済 and reverts the line back to 未発注, then applies the new supplier.
  const changeSupplierForced = useCallback(async (line_index, supplier_id) => {
    const sname = supplier_id ? ((suppliers || []).find((s) => s.id === supplier_id)?.name || '') : '未指定';
    try {
      const res = await shizaiApi.patchLineSupplier(req.id, line_index, supplier_id || null, true);
      const poNote = res?.cancelled_po_no ? `（発注 ${res.cancelled_po_no} は取消されました）` : '';
      toast.success(`#${line_index + 1} の仕入先を「${sname}」に変更しました${poNote}`);
      setSuggestByLine((prev) => {
        const next = { ...prev };
        delete next[line_index];
        return next;
      });
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '仕入先の変更に失敗しました');
    }
  }, [req.id, suppliers, onReload]);

  // 一括割り当て: assign every open 仕入先未指定 line to one picked supplier.
  const [bulkSup, setBulkSup] = useState('');
  const noSupTargets = (req.lines || [])
    .map((ln, i) => ({ ln, i }))
    .filter(({ ln }) => ln.line_status !== 'ordered' && !ln.supplier_id);
  const bulkAssign = async () => {
    if (!bulkSup || noSupTargets.length === 0) return;
    const sname = (suppliers || []).find((s) => s.id === bulkSup)?.name || '';
    try {
      await Promise.all(noSupTargets.map(({ i }) => shizaiApi.patchLineSupplier(req.id, i, bulkSup)));
      toast.success(`仕入先未指定の ${noSupTargets.length}明細 を「${sname}」に割り当てました`);
      setBulkSup('');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一括割り当てに失敗しました');
    }
  };

  return (
    <div
      data-testid={`request-row-${req.id}`}
      data-source={req.source || 'buyer'}
      className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${
        isParts ? 'border-orange-200 ring-1 ring-orange-100' : 'border-zinc-200'
      }`}
    >
      {/* Summary row */}
      <div className="p-4 flex items-start gap-3">
        {canWrite && onSelectToggle && (
          <input
            type="checkbox"
            checked={selected || false}
            onChange={(e) => onSelectToggle(e.target.checked)}
            className="mt-2 shrink-0 h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
            data-testid={`request-select-${req.id}`}
            title="複数選択"
          />
        )}
        <button
          type="button"
          onClick={onExpand}
          data-testid={`request-expand-${req.id}`}
          className="mt-0.5 shrink-0 w-7 h-7 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-500"
          title={expanded ? t('shizai.req.collapse') : t('shizai.req.expand')}
        >
          {expanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
        </button>

        <div className="flex-1 min-w-0">
          {/* First row: badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-zinc-500">{req.request_no}</span>
            {isParts ? (
              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300 font-semibold" data-testid={`request-source-${req.id}`}>
                <Storefront size={11} weight="bold" className="mr-1" /> {t('shizai.req.sourceParts')}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]" data-testid={`request-source-${req.id}`}>
                {t('shizai.req.sourceBuyer')}
              </Badge>
            )}
            <Badge variant="outline" className={statusCls}>{t(`shizai.reqStatus.${req.status}`)}</Badge>
            <Badge variant="outline" className={priorityCls}>{t('shizai.req.priority')}: {t(`shizai.priority.${req.priority || 'normal'}`)}</Badge>
            {req.order_type === 'subcontract' && (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200" data-testid={`request-type-${req.id}`}>
                {t('shizai.orderTypeShort.subcontract')}
              </Badge>
            )}
            {req.department_name && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold text-white align-middle"
                style={{ backgroundColor: req.department_color || '#a1a1aa' }}
                data-testid={`request-dept-${req.id}`}
              >
                {req.department_code || req.department_name}
              </span>
            )}
            {req.order_name && (
              <span
                className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 text-[10px]"
                data-testid={`request-order-name-${req.id}`}
                title={t('shizai.req.orderName')}
              >
                受注: {req.order_name}
              </span>
            )}
            {/* Editable 受注分類 chip — auto-generated REQs (WO/補充提案/
                parts_request) usually have this empty. Buyers click to
                assign / re-assign via a small dialog. Non-writers still
                see the read-only chip. */}
            {req.order_category_name ? (
              canWrite ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEditCategory && onEditCategory(req); }}
                  className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 text-[10px] transition-colors inline-flex items-center gap-1"
                  data-testid={`request-order-cat-${req.id}`}
                  title="受注分類を変更"
                >
                  {req.order_category_name}
                  {req.order_category_code ? ` (${req.order_category_code})` : ''}
                  <PencilSimple size={9} weight="bold" className="opacity-60" />
                </button>
              ) : (
                <span
                  className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[10px]"
                  data-testid={`request-order-cat-${req.id}`}
                  title={t('shizai.req.orderCategory')}
                >
                  {req.order_category_name}
                  {req.order_category_code ? ` (${req.order_category_code})` : ''}
                </span>
              )
            ) : canWrite && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEditCategory && onEditCategory(req); }}
                className="px-1.5 py-0.5 rounded border border-dashed border-zinc-300 text-zinc-500 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-300 text-[10px] transition-colors inline-flex items-center gap-1"
                data-testid={`request-order-cat-add-${req.id}`}
                title="受注分類を設定"
              >
                <Plus size={10} weight="bold" />
                受注分類
              </button>
            )}
            {attachCount > 0 && (
              <span
                className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[10px] inline-flex items-center gap-1"
                data-testid={`request-attach-count-${req.id}`}
                title={t('shizai.req.attachments')}
              >
                <FilePdf size={10} weight="bold" /> {attachCount}
              </span>
            )}
            {(req.line_suppliers || []).length > 1 && (
              <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-semibold"
                data-testid={`request-multisup-${req.id}`}
                title={`未発注の仕入先 ${(req.line_suppliers || []).length}社`}>
                {(req.line_suppliers || []).length}社
              </span>
            )}
            {(req.lines_total_count || 0) > 1 && (req.lines_ordered_count || 0) > 0 && (req.lines_ordered_count < req.lines_total_count) && (
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-semibold"
                data-testid={`request-partial-${req.id}`}
                title="一部の明細のみ発注済">
                {req.lines_ordered_count}/{req.lines_total_count} 発注済
              </span>
            )}
            {(req.lines_total_count || 0) > 0 && (req.linked_lines_count || 0) > 0 && (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  req.linked_lines_count === req.lines_total_count
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-zinc-100 text-zinc-600 border border-zinc-300'
                }`}
                data-testid={`request-linked-${req.id}`}
                title={req.linked_lines_count === req.lines_total_count
                  ? '全ての明細が品目マスターとリンク済'
                  : 'マスター未リンクの明細があります'}>
                マスター {req.linked_lines_count}/{req.lines_total_count}
              </span>
            )}
            {/* Techs入力済み chip — 依頼内容をレガシー Techs システムに転記
                したかのトラッキング。買い手(canWrite)はクリックで切替。 */}
            {req.techs_entered ? (
              canWrite ? (
                <button
                  type="button"
                  onClick={toggleTechs}
                  disabled={techsBusy}
                  className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-[10px] font-semibold inline-flex items-center gap-1 transition-colors"
                  data-testid={`request-techs-${req.id}`}
                  title={`Techs入力済み（${req.techs_entered_by || ''} ${String(req.techs_entered_at || '').slice(0, 10)}）— クリックで未入力に戻す`}
                >
                  <CheckSquare size={10} weight="fill" /> Techs入力済{req.techs_order_no ? ` · ${req.techs_order_no}` : ''}
                </button>
              ) : (
                <span
                  className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold inline-flex items-center gap-1"
                  data-testid={`request-techs-${req.id}`}
                  title={`Techs入力済み（${req.techs_entered_by || ''} ${String(req.techs_entered_at || '').slice(0, 10)}）`}
                >
                  <CheckSquare size={10} weight="fill" /> Techs入力済{req.techs_order_no ? ` · ${req.techs_order_no}` : ''}
                </span>
              )
            ) : canWrite && (
              <button
                type="button"
                onClick={toggleTechs}
                disabled={techsBusy}
                className="px-1.5 py-0.5 rounded border border-dashed border-zinc-300 text-zinc-500 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 text-[10px] inline-flex items-center gap-1 transition-colors"
                data-testid={`request-techs-${req.id}`}
                title="クリックで Techs入力済み にする"
              >
                <Square size={10} weight="bold" /> Techs未入力
              </button>
            )}
          </div>

          {/* Second row: item summary — WO-generated REQs show the product being
              manufactured («SS-45BMC用基板 用の部品»); manual REQs fall back to
              the first line's name/part_no. */}
          <div className="mt-1.5 flex items-baseline gap-2 flex-wrap min-w-0">
            {req.product_name ? (
              <>
                <span className="font-medium text-zinc-900 truncate" data-testid={`request-product-name-${req.id}`}>
                  {req.product_name} 用の部品
                </span>
                <span className="text-xs text-zinc-500">（{req.line_count || (req.lines || []).length} 明細）</span>
              </>
            ) : (
              <>
                <span className="font-medium text-zinc-900 truncate">
                  {req.first_line_name || req.name || first.name || '—'}
                </span>
                {otherCount > 0 && <span className="text-xs text-zinc-500">他 {otherCount} 件</span>}
                <span className="text-xs text-zinc-500 font-mono">{req.first_line_part_no || first.part_no || ''}</span>
              </>
            )}
          </div>

          {/* Third row: meta */}
          <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-zinc-500">
            <span className="tabular-nums">{req.total_qty_by_uom || `${req.qty || 0} ${req.uom || '個'}`}</span>
            {req.needed_by && <span>{t('shizai.req.neededBy')}: {req.needed_by}</span>}
            {(isParts || req.mfg_order_id) && req.requested_by && (
              <span className="inline-flex items-center gap-1" data-testid={`request-requester-${req.id}`}
                    title={req.mfg_order_id ? '作業指示を開始した担当者' : '依頼者'}>
                <UserIcon size={11} weight="bold" /> {req.requested_by}
              </span>
            )}
            {anyAttach && !expanded && (
              <button
                type="button"
                onClick={onExpand}
                className="text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 font-semibold"
                data-testid={`request-attach-toggle-${req.id}`}
              >
                添付ファイル・詳細を表示
              </button>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-start gap-1 shrink-0">
          {canWrite && canOrder && (
            <Button
              size="sm"
              className={`h-9 shadow-sm ${isParts ? 'bg-teal-700 hover:bg-teal-800' : 'bg-teal-700 hover:bg-teal-800'}`}
              data-testid={`request-order-${req.id}`}
              onClick={onPlace}
            >
              {t('shizai.order.placeOrder')} <ArrowRight size={13} className="ml-1" weight="bold" />
            </Button>
          )}
          {canWrite && canOrder && (
            <Button size="sm" variant="ghost" data-testid={`request-edit-${req.id}`} onClick={onEdit} className="h-9 px-2 text-zinc-600">
              <PencilSimple size={16} />
            </Button>
          )}
          {/* iter_143h · A4 発注依頼書 print — opens a full-screen receipt
              in a new tab (works whether the request came from 部品受注入力
              or was created directly by the buyer). */}
          <Button
            size="sm"
            variant="ghost"
            data-testid={`request-print-${req.id}`}
            onClick={() => window.open(`/shizai/requests/${req.id}/print`, '_blank', 'noopener,noreferrer')}
            className="h-9 px-2 text-indigo-600 hover:bg-indigo-50"
            title="発注依頼書をA4で印刷"
          >
            <Printer size={16} />
          </Button>
          {canWrite && canOrder && (
            <Button size="sm" variant="ghost" data-testid={`request-delete-${req.id}`} onClick={onDelete} className="h-9 px-2 text-red-600 hover:bg-red-50">
              <Trash size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-zinc-200 bg-zinc-50/60 px-4 py-3 space-y-2" data-testid={`request-detail-${req.id}`}>
          {/* 一括割り当てバー — 仕入先未指定の明細が2件以上あるとき表示 */}
          {canWrite && noSupTargets.length >= 2 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 flex items-center gap-2 flex-wrap"
              data-testid={`request-bulk-supplier-row-${req.id}`}>
              <span className="text-[11px] font-semibold text-amber-900 shrink-0">
                仕入先未指定 {noSupTargets.length}明細
              </span>
              <select
                value={bulkSup}
                onChange={(e) => setBulkSup(e.target.value)}
                data-testid={`request-bulk-supplier-select-${req.id}`}
                className="h-8 max-w-[220px] rounded-md border border-amber-300 bg-white px-2 text-[11px] font-medium text-zinc-800 outline-none focus:ring-2 focus:ring-amber-200"
              >
                <option value="">仕入先を選択...</option>
                {(suppliers || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={bulkAssign}
                disabled={!bulkSup}
                data-testid={`request-bulk-supplier-btn-${req.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-40"
                title="仕入先未指定の明細をすべて、選択した仕入先に割り当てます"
              >
                <Lightning size={11} weight="fill" />
                まとめて割り当て
              </button>
            </div>
          )}
          {(req.lines || []).map((ln, i) => (
            <RequestLineDetail
              key={`${req.id}-ln-${i}`}
              idx={i}
              line={ln}
              reqId={req.id}
              canWrite={canWrite}
              suggestions={suggestByLine[i]}
              suggestLoading={suggestLoading}
              onApplySupplier={applySupplier}
              suppliers={suppliers}
              onChangeSupplier={changeSupplier}
              onChangeSupplierForced={changeSupplierForced}
            />
          ))}
          {req.notes && (
            <div className="text-xs text-zinc-600 px-1" data-testid={`request-notes-${req.id}`}>
              <span className="font-semibold text-zinc-500">{t('shizai.req.notes')}:</span> {req.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RequestLineDetail({ idx, line, reqId, canWrite, suggestions, suggestLoading, onApplySupplier, suppliers, onChangeSupplier, onChangeSupplierForced }) {
  const REASON_META = {
    preferred:      { Icon: Star,      cls: 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100', label: '推奨' },
    last_po:        { Icon: Lightning, cls: 'bg-sky-50 text-sky-800 border-sky-300 hover:bg-sky-100',         label: '前回' },
    history_count:  { Icon: Lightning, cls: 'bg-violet-50 text-violet-800 border-violet-300 hover:bg-violet-100', label: '履歴' },
  };
  // Feb 2026: confirm-first flow for changing supplier on 発注済 lines.
  // Selecting from the ordered-line select opens a small AlertDialog to warn
  // that the associated PO will be cancelled before the change lands.
  const [pendingForcedSup, setPendingForcedSup] = useState(null);
  const isOrdered = line.line_status === 'ordered';
  const pickedName = pendingForcedSup
    ? ((suppliers || []).find((s) => s.id === pendingForcedSup)?.name || '未指定')
    : '';
  return (
    <div className="bg-white rounded-lg border border-zinc-200 p-3 flex items-start gap-3" data-testid={`request-line-detail-${reqId}-${idx}`}>
      {line.photo_url && (
        line.photo_is_pdf ? (
          <StorageLink
            path={line.photo_url}
            target="_blank" rel="noreferrer"
            className="w-14 h-14 rounded-md bg-rose-50 border border-rose-200 flex flex-col items-center justify-center shrink-0 hover:bg-rose-100"
            title={line.photo_filename || 'PDFを開く'}
            data-testid={`request-attach-${reqId}-${idx}`}
          >
            <FilePdf size={20} weight="duotone" className="text-rose-600" />
            <span className="text-[9px] font-bold text-rose-700 mt-0.5">PDF</span>
          </StorageLink>
        ) : (
          <StorageLink
            path={line.photo_url}
            target="_blank" rel="noreferrer"
            className="shrink-0"
            title={line.photo_filename || '画像を開く'}
            data-testid={`request-attach-${reqId}-${idx}`}
          >
            <StorageThumb
              path={line.photo_url}
              alt=""
              className="w-14 h-14 rounded-md object-cover border border-zinc-200 hover:opacity-90"
            />
          </StorageLink>
        )
      )}
      {!line.photo_url && (
        <div className="w-14 h-14 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0 text-zinc-300">
          <ImageSquare size={20} weight="duotone" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-zinc-400">#{idx + 1}</span>
          <span className="font-medium text-zinc-800 truncate">{line.name || line.part_no || '—'}</span>
          <span className="tabular-nums text-xs text-zinc-500 shrink-0">{line.qty} {line.uom}</span>
          {canWrite && !isOrdered && (suppliers || []).length > 0 ? (
            <span className="inline-flex items-center gap-1 shrink-0">
              <span className="text-[10px] font-semibold text-zinc-400">仕入先:</span>
              <select
                value={line.supplier_id || ''}
                onChange={(e) => onChangeSupplier(idx, e.target.value || null)}
                data-testid={`request-line-supplier-select-${reqId}-${idx}`}
                className={`h-6 max-w-[200px] rounded-md border px-1.5 text-[10px] font-medium outline-none focus:ring-2 focus:ring-rose-200 ${
                  line.supplier_id ? 'border-zinc-300 bg-white text-zinc-800' : 'border-amber-300 bg-amber-50 text-amber-800'
                }`}
                title="この明細の仕入先を変更します"
              >
                <option value="">未指定</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</option>
                ))}
              </select>
            </span>
          ) : canWrite && isOrdered && (suppliers || []).length > 0 ? (
            // Feb 2026: 発注済み明細でも変更可 — 発注は自動取消される
            <span className="inline-flex items-center gap-1 shrink-0">
              {line.supplier_name && (
                <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 text-[10px]"
                  data-testid={`request-line-supplier-${reqId}-${idx}`}
                  title="現在の仕入先">
                  {line.supplier_name}
                </span>
              )}
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  setPendingForcedSup(v);
                }}
                data-testid={`request-line-supplier-force-select-${reqId}-${idx}`}
                className="h-6 max-w-[160px] rounded-md border border-amber-400 bg-amber-50 text-amber-800 px-1.5 text-[10px] font-medium outline-none focus:ring-2 focus:ring-amber-200"
                title="発注済み — 変更すると発注は自動取消されます"
              >
                <option value="">仕入先を変更…</option>
                {suppliers.filter((s) => s.id !== line.supplier_id).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</option>
                ))}
              </select>
            </span>
          ) : line.supplier_name && (
            <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 text-[10px]"
              data-testid={`request-line-supplier-${reqId}-${idx}`}
              title="仕入先">
              {line.supplier_name}
            </span>
          )}
          {isOrdered && (
            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-semibold"
              data-testid={`request-line-status-ordered-${reqId}-${idx}`}>
              発注済
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap text-xs text-zinc-500">
          {(line.unit_records || []).map((u) => (
            <span key={u.id} className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 font-mono text-[10px] text-emerald-800"
              data-testid={`request-line-unit-${reqId}-${idx}-${u.no}`} title={`${u.label}として登録済み`}>
              {u.no}
            </span>
          ))}
          {line.part_no && <span className="font-mono">品番: {line.part_no}</span>}
          {line.model_no && <span className="font-mono">型式: {line.model_no}</span>}
          {line.maker && <span>メーカー: {line.maker}</span>}
          {line.maker_pn && <span className="font-mono">MPN: {line.maker_pn}</span>}
          {line.material && <span>材質: {line.material}</span>}
          {line.order_category_name && (
            <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[10px]">
              {line.order_category_name}
              {line.order_category_code ? ` (${line.order_category_code})` : ''}
            </span>
          )}
        </div>
        {/* Per-line supplier suggestion chips (Feb 2026) — visible only when
            (a) buyer has edit rights, (b) line is open, (c) no supplier
            already picked, (d) backend returned candidates. Click applies
            the pick and refreshes the parent. */}
        {canWrite
          && line.line_status !== 'ordered'
          && !line.supplier_id
          && (suggestions || []).length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`request-line-suggest-${reqId}-${idx}`}>
            <span className="text-[10px] font-semibold text-zinc-500 shrink-0">仕入先候補:</span>
            {suggestions.map((c) => {
              const meta = REASON_META[c.reason] || REASON_META.history_count;
              const Icon = meta.Icon;
              return (
                <button
                  key={c.supplier_id}
                  type="button"
                  onClick={() => onApplySupplier(idx, c.supplier_id, c.supplier_name)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${meta.cls}`}
                  data-testid={`request-line-suggest-chip-${reqId}-${idx}-${c.reason}`}
                  title={c.detail}
                >
                  <Icon size={10} weight="fill" />
                  <span className="font-semibold"><span data-no-translate>{c.supplier_name}</span></span>
                  <span className="opacity-70 text-[9px]">{c.detail}</span>
                </button>
              );
            })}
          </div>
        )}
        {canWrite
          && line.line_status !== 'ordered'
          && !line.supplier_id
          && suggestLoading
          && (suggestions || []).length === 0 && (
          <div className="mt-2 text-[10px] text-zinc-400" data-testid={`request-line-suggest-loading-${reqId}-${idx}`}>
            仕入先候補を読み込み中...
          </div>
        )}
        {line.reference_url && (
          <a
            href={line.reference_url}
            target="_blank" rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 truncate max-w-full"
            data-testid={`request-ref-url-${reqId}-${idx}`}
          >
            <LinkIcon size={11} weight="bold" /> {line.reference_url}
          </a>
        )}
        {line.notes && <div className="mt-1 text-xs text-zinc-500">{line.notes}</div>}
      </div>
      {/* Feb 2026: confirm dialog before force-changing supplier on 発注済 lines */}
      <Dialog open={!!pendingForcedSup} onOpenChange={(o) => !o && setPendingForcedSup(null)}>
        <DialogContent className="max-w-md" data-testid={`request-force-change-dialog-${reqId}-${idx}`}>
          <DialogHeader>
            <DialogTitle>発注済み明細の仕入先変更</DialogTitle>
            <DialogDescription className="sr-only">
              発注済みの明細の仕入先を変更します。現在紐付けられている発注は自動的に取消されます。
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-zinc-700 space-y-2">
            <p>
              <span className="font-mono text-zinc-500">#{idx + 1}</span>{' '}
              <span className="font-medium">{line.name || line.part_no || '—'}</span> の仕入先を
              <br />
              <span className="text-zinc-500 line-through">{line.supplier_name || '未指定'}</span>
              {' → '}
              <span className="font-semibold text-rose-700">{pickedName}</span>
              {' '}に変更します。
            </p>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              現在紐付けられている発注 (PO) は自動的に <strong>取消</strong> されます。
              変更後、新しい仕入先で改めて発注してください。
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingForcedSup(null)}
              data-testid={`request-force-change-cancel-${reqId}-${idx}`}>
              キャンセル
            </Button>
            <Button
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid={`request-force-change-confirm-${reqId}-${idx}`}
              onClick={() => {
                const sid = pendingForcedSup;
                setPendingForcedSup(null);
                onChangeSupplierForced && onChangeSupplierForced(idx, sid);
              }}
            >
              発注を取消して変更
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
